import * as THREE from 'three';
import { basic, emis, metal, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Bruni, the Hollow Forger (docs/ANTIHEROES.md): a dwarf of Hilde's clan who died at the forge of Somnhold. The titan
 * dreams him short and broad as an anvil, in black glass plate cracked with the cold blue of a forge arc, a beard of
 * glass braided in iron rings, two arcs for eyes under a squat helm with a chimney crest. On his back a forge — a
 * grated furnace and its pipe — feeds a stubby cannon over his right shoulder; in his right hand a heavy hammer whose
 * head glows through its cracks; on his left a great riveted gauntlet. A mantle of smoke hangs off the furnace.
 *
 * Built on the fallen sentinel's bones (anim/creatures/bruni.ts reuses its walk, reel, stagger and fall), with the
 * same names as Corvin's: body, chest, head, flapF/flapB, armL/foreL/shield (the gauntlet), armR/foreR/sword (the
 * hammer), legs (thigh, shin). Faces +Z, `R` limbs on +X.
 */

/** Model height (the chimney crest's top). */
export const BRUNI_HEIGHT = 1.8;

export const bruni = (o?: BaseOpts): Model => {
  const c = begin(o, 0.7, 911);
  const { g, rng } = c;
  const k4 = kit(rng);

  const glass = metal('#2c303b', { roughness: 0.16, metalness: 0.5, bumpScale: 0.01 });
  const glassD = metal('#1a1c23', { roughness: 0.28, metalness: 0.5, bumpScale: 0.02 });
  const mail = std('#16171c', { roughness: 0.65, metalness: 0.35, bumpScale: 0.06 });
  const iron = metal('#3a3f47', { roughness: 0.5, metalness: 0.7, bumpScale: 0.04 });
  const soot = std('#141517', { roughness: 0.9, bumpScale: 0.08 });
  const leather = std('#1d1a1a', { roughness: 0.85, bumpScale: 0.05 });
  const arc = emis('#c8f6ff', '#22c4ff', 3.0);
  const arcF = emis('#c8f6ff', '#4fe0ff', 2.2, { transparent: true, opacity: 0.85, depthWrite: false });
  const smokeM = basic('#1a2226', 0.55);

  const bone = (parent: THREE.Object3D, at: THREE.Vector3, name: string): { p: THREE.Group; abs: THREE.Group } => joint(parent, at, name);

  /* ---------- legs: short and thick, glass greaves, broad iron-shod boots ---------- */
  const legs: Record<string, THREE.Object3D> = {};
  for (const k of [-1, 1]) {
    const s = k < 0 ? 'L' : 'R';
    const legB = bone(g, V(k * 0.2, 0.64, 0), `leg${s}`);
    const hip = legB.abs;
    limb(hip, V(k * 0.2, 0.66, 0), V(k * 0.22, 0.36, 0.02), 0.15, 0.125, glass, 7);
    k4.crack(hip, [V(k * 0.25, 0.6, 0.12), V(k * 0.27, 0.5, 0.13), V(k * 0.24, 0.41, 0.12)], 0.008, arc, 0.03);
    const shinB = bone(hip, V(k * 0.22, 0.35, 0.02), `shin${s}`);
    const lo = shinB.abs;
    k4.swell(lo, glassD, k * 0.22, 0.35, 0.09, 0.1, 0.09, 0.085, 7, 4);
    M(new THREE.SphereGeometry(0.022, 5, 4), arc, lo).position.set(k * 0.22, 0.35, 0.175);
    limb(lo, V(k * 0.22, 0.33, 0.02), V(k * 0.22, 0.1, 0.0), 0.125, 0.11, glass, 7);
    const boot = M(new THREE.BoxGeometry(0.24, 0.12, 0.34), iron, lo);
    boot.position.set(k * 0.22, 0.06, 0.06);
    const toe = M(new THREE.CylinderGeometry(0.12, 0.12, 0.24, 8, 1, false, 0, Math.PI), iron, lo);
    toe.rotation.set(0, 0, Math.PI / 2);
    toe.position.set(k * 0.22, 0.06, 0.22);
    toe.scale.set(0.55, 1, 0.9);
    legs[`leg${s}`] = legB.p;
    legs[`shin${s}`] = shinB.p;
  }

  /* ---------- hips: mail skirt, a thick belt with an anvil buckle, a leather apron in front ---------- */
  const bodyB = bone(g, V(0, 0.66, 0), 'body');
  const body = bodyB.abs;
  M(new THREE.CylinderGeometry(0.36, 0.42, 0.28, 10, 1, true), mail, body).position.set(0, 0.58, 0);
  const fauld = M(new THREE.CylinderGeometry(0.36, 0.39, 0.16, 10), glassD, body);
  fauld.position.set(0, 0.72, 0);
  const belt = M(new THREE.TorusGeometry(0.37, 0.035, 4, 14), iron, body);
  belt.rotation.x = Math.PI / 2;
  belt.position.set(0, 0.8, 0);
  belt.scale.set(1, 0.82, 1);
  const buckle = G(body, 0, 0.8, 0.33);
  M(new THREE.BoxGeometry(0.18, 0.05, 0.06), iron, buckle).position.y = 0.03;
  M(new THREE.BoxGeometry(0.08, 0.06, 0.05), iron, buckle);
  M(new THREE.BoxGeometry(0.14, 0.03, 0.05), iron, buckle).position.y = -0.04;
  const flapF = bone(body, V(0, 0.78, 0.3), 'flapF');
  k4.panel(flapF.abs, V(0, 0.78, 0.32), 0.36, 0.52, 0.02, 1, leather, 0.3);
  const flapB = bone(body, V(0, 0.78, -0.3), 'flapB');
  k4.panel(flapB.abs, V(0, 0.78, -0.32), 0.42, 0.5, 0.02, -1, leather, 0.4);

  /* ---------- chest: a barrel of black glass split by blue arcs, broad pauldrons ---------- */
  const chestB = bone(body, V(0, 0.82, 0), 'chest');
  const chest = chestB.abs;
  k4.swell(chest, glass, 0, 1.06, 0.0, 0.42, 0.33, 0.33, 10, 6);
  k4.swell(chest, glassD, 0, 0.88, 0.01, 0.37, 0.13, 0.3, 9, 4);
  k4.crack(chest, [V(0.22, 1.28, 0.24), V(0.12, 1.16, 0.3), V(0.02, 1.08, 0.32), V(-0.08, 0.98, 0.31), V(-0.04, 0.88, 0.29), V(-0.15, 0.8, 0.26)], 0.015, arc, 0.06);
  k4.crack(chest, [V(0.02, 1.08, 0.32), V(0.15, 1.0, 0.3), V(0.24, 0.92, 0.25)], 0.01, arc, 0.04);
  glowSprite(chest, '#4fe0ff', 0.45, V(0.02, 1.07, 0.36), 0.55);
  // Rivets across the plate's rim.
  for (let i = 0; i < 7; i++) {
    const a = -0.9 + (i / 6) * 1.8;
    M(new THREE.SphereGeometry(0.02, 4, 3), iron, chest).position.set(Math.sin(a) * 0.4, 1.28 - Math.abs(a) * 0.05, Math.cos(a) * 0.27);
  }
  for (const k of [-1, 1]) {
    const pd = G(chest, k * 0.44, 1.26, 0);
    pd.rotation.z = -k * 0.28;
    const dome = M(new THREE.SphereGeometry(1, 9, 4, 0, Math.PI * 2, 0, Math.PI / 2), glass, pd);
    dome.scale.set(0.24, 0.16, 0.25);
    for (let i = 1; i <= 2; i++) {
      const lame = M(new THREE.CylinderGeometry(0.24 - i * 0.012, 0.25 - i * 0.01, 0.06, 9, 1, true), i === 1 ? glassD : glass, pd);
      lame.position.y = -0.04 - i * 0.055;
    }
    M(new THREE.SphereGeometry(0.025, 5, 4), iron, pd).position.set(0, 0.15, 0.06);
  }

  /* ---------- on his back: the forge — a grated furnace, its pipe, the cannon over his right shoulder ---------- */
  const forge = G(chest, 0, 1.02, -0.34);
  {
    const box = M(new THREE.BoxGeometry(0.46, 0.5, 0.26), iron, forge);
    box.position.set(0, 0, -0.06);
    M(new THREE.BoxGeometry(0.5, 0.06, 0.3), soot, forge).position.set(0, 0.27, -0.06);
    M(new THREE.BoxGeometry(0.5, 0.06, 0.3), soot, forge).position.set(0, -0.27, -0.06);
    // The grate: bars over a blue fire.
    M(new THREE.BoxGeometry(0.3, 0.22, 0.02), arc, forge).position.set(0, -0.04, -0.2);
    for (let i = 0; i < 5; i++) M(new THREE.BoxGeometry(0.025, 0.26, 0.03), soot, forge).position.set(-0.12 + i * 0.06, -0.04, -0.21);
    glowSprite(forge, '#4fe0ff', 0.6, V(0, -0.04, -0.28), 0.6);
    // The pipe rising off its top, crooked, a lip at the end.
    limb(forge, V(-0.14, 0.28, -0.08), V(-0.18, 0.62, -0.12), 0.05, 0.045, soot, 7);
    limb(forge, V(-0.18, 0.6, -0.12), V(-0.12, 0.76, -0.16), 0.045, 0.045, soot, 7);
    M(new THREE.CylinderGeometry(0.065, 0.05, 0.05, 8), iron, forge).position.set(-0.12, 0.78, -0.16);
    // Straps over the shoulders.
    for (const k of [-1, 1]) {
      const st = M(new THREE.BoxGeometry(0.07, 0.04, 0.42), leather, forge);
      st.position.set(k * 0.18, 0.28, 0.16);
      st.rotation.x = -0.25;
    }
  }
  const cannon = G(chest, 0.5, 1.56, -0.14);
  cannon.rotation.set(-0.12, 0, 0);
  {
    // Breech, the stubby barrel bound in iron hoops, a flared muzzle with the arc inside.
    M(new THREE.SphereGeometry(0.12, 9, 6), iron, cannon).position.set(0, 0, -0.32);
    const barrel = M(new THREE.CylinderGeometry(0.1, 0.12, 0.72, 10), glassD, cannon);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = 0.02;
    for (const z of [-0.22, 0.02, 0.26]) {
      const hoop = M(new THREE.TorusGeometry(0.115, 0.022, 4, 12), iron, cannon);
      hoop.position.z = z;
    }
    const muzzle = M(new THREE.CylinderGeometry(0.135, 0.1, 0.1, 10, 1, true), iron, cannon);
    muzzle.rotation.x = Math.PI / 2;
    muzzle.position.z = 0.42;
    const bore = M(new THREE.CircleGeometry(0.075, 10), arc, cannon);
    bore.position.z = 0.4;
    glowSprite(cannon, '#4fe0ff', 0.3, V(0, 0, 0.47), 0.6);
    k4.crack(cannon, [V(0.06, 0.08, -0.15), V(0.09, 0.06, 0.0), V(0.07, 0.08, 0.14)], 0.008, arc, 0.02);
    // The mount from the forge.
    limb(cannon, V(-0.18, -0.12, -0.3), V(0, -0.06, -0.22), 0.03, 0.03, iron, 6);
  }

  /* ---------- the mantle of smoke, off the furnace ---------- */
  const mantle = G(chest, 0, 1.24, -0.42);
  for (const [x, w, h, rz] of [[-0.2, 0.3, 0.95, 0.08], [0.2, 0.3, 0.9, -0.08], [0, 0.34, 1.0, 0]] as const) {
    const pn = k4.panel(mantle, V(x, 0, -0.06), w, h, 0.02, -1, smokeM, 0.6);
    pn.rotation.set(0.1, 0, rz);
  }

  /* ---------- head: a squat helm with a chimney crest, arcs for eyes, a braided beard of glass ---------- */
  const headB = bone(chest, V(0, 1.32, 0.04), 'head');
  const head = headB.abs;
  // A dwarf's head: big for his height.
  headB.p.scale.setScalar(1.2);
  M(new THREE.CylinderGeometry(0.16, 0.17, 0.2, 10), glass, head).position.set(0, 1.44, 0.05);
  const dome = M(new THREE.SphereGeometry(0.165, 10, 5, 0, Math.PI * 2, 0, Math.PI / 2), glass, head);
  dome.position.set(0, 1.53, 0.05);
  const brim = M(new THREE.TorusGeometry(0.17, 0.025, 4, 14), iron, head);
  brim.rotation.x = Math.PI / 2;
  brim.position.set(0, 1.52, 0.05);
  const nasal = M(new THREE.BoxGeometry(0.035, 0.15, 0.03), glassD, head);
  nasal.position.set(0, 1.44, 0.22);
  for (const k of [-1, 1]) {
    M(new THREE.SphereGeometry(0.025, 6, 4), arc, head).position.set(k * 0.065, 1.46, 0.2);
    glowSprite(head, '#8aeaff', 0.24, V(k * 0.065, 1.46, 0.23), 0.95);
    const cheek = M(new THREE.BoxGeometry(0.05, 0.13, 0.12), glassD, head);
    cheek.position.set(k * 0.15, 1.39, 0.1);
  }
  // The crest: a little forge chimney on the helm's crown, an arc flickering out of it.
  const crest = G(head, 0, 1.68, 0.03);
  M(new THREE.CylinderGeometry(0.045, 0.06, 0.12, 8), iron, crest);
  M(new THREE.CylinderGeometry(0.06, 0.05, 0.03, 8), soot, crest).position.y = 0.07;
  const flames: THREE.Mesh[] = [];
  for (const [x, z, s, h] of [[0, 0, 1, 0.16], [0.025, -0.02, 0.7, 0.11], [-0.025, 0.015, 0.6, 0.1]] as const) {
    const f = M(new THREE.ConeGeometry(0.035 * s, h, 6), arcF, crest);
    f.position.set(x, 0.08 + h / 2, z);
    flames.push(f);
  }
  glowSprite(crest, '#4fe0ff', 0.4, V(0, 0.14, 0), 0.6);
  // The beard: a broad wedge of glass over the chest, two braids bound in iron rings, an arc down its middle.
  const beard = M(new THREE.ConeGeometry(0.17, 0.42, 6), glassD, head);
  beard.position.set(0, 1.17, 0.33);
  beard.rotation.set(Math.PI + 0.25, 0, 0);
  beard.scale.set(1.15, 1, 0.55);
  k4.crack(head, [V(0, 1.34, 0.36), V(0.01, 1.22, 0.38), V(-0.01, 1.08, 0.36)], 0.008, arc, 0.02);
  for (const k of [-1, 1]) {
    limb(head, V(k * 0.1, 1.3, 0.32), V(k * 0.12, 0.98, 0.36), 0.035, 0.022, glassD, 5);
    for (const y of [1.18, 1.06]) {
      const r = M(new THREE.TorusGeometry(0.038, 0.012, 3, 8), iron, head);
      r.rotation.x = Math.PI / 2;
      r.position.set(k * (0.11 + (1.3 - y) * 0.06), y, 0.34 + (1.3 - y) * 0.12);
    }
  }

  /* ---------- left arm: a great riveted gauntlet ---------- */
  const armLB = bone(chest, V(-0.45, 1.2, 0), 'armL');
  const aL = armLB.abs;
  limb(aL, V(-0.46, 1.2, 0), V(-0.52, 0.92, 0.05), 0.105, 0.09, glass, 6);
  const foreLB = bone(aL, V(-0.52, 0.91, 0.06), 'foreL');
  const fL = foreLB.abs;
  k4.swell(fL, glassD, -0.52, 0.91, 0.06, 0.09, 0.09, 0.09, 6, 4);
  limb(fL, V(-0.52, 0.91, 0.06), V(-0.46, 0.7, 0.2), 0.095, 0.085, glass, 6);
  const shieldB = bone(fL, V(-0.45, 0.67, 0.22), 'shield');
  const sh = shieldB.abs;
  {
    const fist = M(new THREE.BoxGeometry(0.2, 0.18, 0.2), iron, sh);
    fist.position.set(-0.45, 0.62, 0.24);
    const cuff = M(new THREE.CylinderGeometry(0.12, 0.13, 0.12, 8), glassD, sh);
    cuff.position.set(-0.46, 0.73, 0.21);
    for (let i = 0; i < 4; i++) M(new THREE.BoxGeometry(0.045, 0.06, 0.06), iron, sh).position.set(-0.52 + i * 0.045, 0.56, 0.33);
    for (let i = 0; i < 4; i++) M(new THREE.SphereGeometry(0.016, 4, 3), arc, sh).position.set(-0.53 + i * 0.05, 0.69, 0.3);
  }

  /* ---------- right arm: the hammer, head down, glowing through its cracks ---------- */
  const armRB = bone(chest, V(0.45, 1.2, 0), 'armR');
  const aR = armRB.abs;
  limb(aR, V(0.46, 1.2, 0), V(0.52, 0.92, 0.03), 0.105, 0.09, glass, 6);
  const foreRB = bone(aR, V(0.52, 0.91, 0.03), 'foreR');
  const fR = foreRB.abs;
  k4.swell(fR, glassD, 0.52, 0.91, 0.03, 0.09, 0.09, 0.09, 6, 4);
  limb(fR, V(0.52, 0.91, 0.03), V(0.54, 0.7, 0.12), 0.09, 0.08, glass, 6);
  const swordB = bone(fR, V(0.54, 0.66, 0.13), 'sword');
  const sw = swordB.abs;
  M(new THREE.BoxGeometry(0.13, 0.13, 0.13), iron, sw).position.set(0.54, 0.66, 0.13);
  {
    const hm = G(sw, 0.54, 0.66, 0.13);
    hm.rotation.x = 0.35;
    M(new THREE.CylinderGeometry(0.028, 0.03, 0.7, 6), leather, hm).position.y = -0.18;
    const headM = M(new THREE.BoxGeometry(0.34, 0.2, 0.2), glassD, hm);
    headM.position.y = -0.56;
    for (const k of [-1, 1]) {
      const face = M(new THREE.BoxGeometry(0.05, 0.23, 0.23), iron, hm);
      face.position.set(k * 0.18, -0.56, 0);
    }
    k4.crack(hm, [V(-0.12, -0.5, 0.105), V(-0.03, -0.58, 0.105), V(0.06, -0.52, 0.105), V(0.13, -0.6, 0.105)], 0.012, arc, 0.02);
    k4.crack(hm, [V(-0.1, -0.6, -0.105), V(0.02, -0.53, -0.105), V(0.1, -0.61, -0.105)], 0.01, arc, 0.02);
    glowSprite(hm, '#4fe0ff', 0.4, V(0, -0.56, 0.14), 0.45);
  }

  /* ---------- smoke breathing off him ---------- */
  const wisps: Ribbon[] = [];
  for (const [x, z, h, y] of [[-0.12, -0.5, 0.75, 1.78], [0.3, -0.3, 0.6, 1.3], [-0.32, -0.3, 0.55, 1.2], [0.05, -0.45, 0.6, 0.7]] as const) {
    wisps.push(new Ribbon(chest, wisp(x, z, h, 4).map((p) => p.add(V(0, y, 0))), 0.05, smokeM, rng, 0.06));
  }

  const t = (n: number): void => {
    for (const r of wisps) r.step();
    flames.forEach((f, i) => {
      const k = ((n * (3 + i * 2) + i * 5) % 7) / 7;
      f.scale.set(0.85 + 0.3 * k, 0.8 + 0.5 * k, 0.85 + 0.3 * k);
    });
  };
  t(0);
  return done(c, BRUNI_HEIGHT, t, undefined, bonesOf(c, {
    body: bodyB.p, chest: chestB.p, head: headB.p, flapF: flapF.p, flapB: flapB.p,
    armL: armLB.p, foreL: foreLB.p, shield: shieldB.p, armR: armRB.p, foreR: foreRB.p, sword: swordB.p,
    legL: legs.legL!, shinL: legs.shinL!, legR: legs.legR!, shinR: legs.shinR!,
  }));
};
